/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ILinkProps, Link } from '@fluentui/react/lib/Link';
import { ITextProps, Text } from '@fluentui/react/lib/Text';
import { Property } from 'csstype';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylist, important } from 'src/core/themes/luisStylist';
import { TitledSection } from 'src/luis/projects/analytics/components/IntentsWithErrorsCard/FaultyIntentDetails/TitledSection';
import { Row, StatsText } from 'src/luis/projects/analytics/components/TrainingEvaluation/Legend/LegendRowHelpers';
import { IntentMetadata } from 'src/luis/projects/analytics/types/IntentMetadata';
import { LuisId } from 'src/luis/types/Resource';

export type DataImbalanceProblemProps = {
    /**
     * The intent that has the most large number of utterances.
     */
    mostGreedyIntent: IntentMetadata;

    /**
     * The selected intent to discuss its problems.
     */
    selectedIntent: IntentMetadata;

    /**
     * Flag to indicate id the selected intent is greedy or needy.
     */
    isNeedyIntent: boolean;

    /**
     * The mean of number of utterances per number of intents
     */
    mean: number;

    /**
     * The intent that has the least number of utterances.
     */
    mostNeedyIntent: IntentMetadata;

    /**
     * A callback for when an intent is clicked.
     */
    onIntentClick?: (intentId: LuisId) => void;
};

const messages = defineMessages({
    dataImbalanceProblemTitle: {
        id: 'analytics.faultyIntentDetails.dataImbalanceProblemTitle',
        defaultMessage: 'Data imbalance'
    },
    needyIntentDescription: {
        id: 'analytics.faultyIntentDetails.needyIntentDescription',
        defaultMessage: ' has fewer example utterances than other intents.'
    },
    needyIntentLink: {
        id: 'analytics.faultyIntentDetails.needyIntentLink',
        defaultMessage: ' Add more utterances to'
    },
    greedyIntentDescription: {
        id: 'analytics.faultyIntentDetails.greedyIntentDescription',
        defaultMessage: ' dominates utterances.'
    },
    greedyIntentLink: {
        id: 'analytics.faultyIntentDetails.greedyIntentLink',
        defaultMessage: ' Consider redistributing utterances on'
    },
    fewestUtterancesLabel: {
        id: 'analytics.faultyIntentDetails.fewestUtterancesLabel',
        defaultMessage: 'Fewest utterances'
    },
    mean: {
        id: 'analytics.faultyIntentDetails.mean',
        defaultMessage: 'Mean'
    },
    mostUtterancesLabel: {
        id: 'analytics.faultyIntentDetails.mostUtterancesLabel',
        defaultMessage: 'Most utterances'
    }
});

const { styleComponent } = getStylist('Dashboard');

const Label = styleComponent<ITextProps>(Text)('Label', theme => ({
    padding: 0,
    fontSize: theme.labelFontSize,
    lineHeight: theme.mediumLineHeight,
    letterSpacing: theme.xLargeLetterSpacing,
    fontWeight: theme.semiboldFont as Property.FontWeight
}));

const IntentName = styleComponent<ITextProps>(Text)('IntentName', theme => ({
    fontSize: theme.labelFontSize,
    lineHeight: theme.mediumLineHeight,
    letterSpacing: theme.xLargeLetterSpacing
}));

const Stats = styleComponent(StatsText)('Stats', theme => ({ paddingLeft: theme.detailedFaultyIntentLegendSpacing }));
const StyledLink = styleComponent<ILinkProps>(Link)('StyledLink', theme =>
    important({ color: theme.correctColor, textDecoration: 'none' })
);

/**
 * Displays the details of the data imbalance problem for the given intent.
 *
 * @param props The properties to visualize the problem.
 */
export const DataImbalanceProblem = (props: DataImbalanceProblemProps) => {
    const { mean, mostNeedyIntent, mostGreedyIntent, selectedIntent, isNeedyIntent, onIntentClick } = props;
    const { localize } = useLocalization();

    const needyIntent = isNeedyIntent ? selectedIntent : mostNeedyIntent;
    const greedyIntent = isNeedyIntent ? mostGreedyIntent : selectedIntent;

    return (
        <TitledSection title={localize(messages.dataImbalanceProblemTitle)}>
            <Text>
                {selectedIntent.intentName}
                {localize(isNeedyIntent ? messages.needyIntentDescription : messages.greedyIntentDescription)}
                <StyledLink onClick={() => onIntentClick(selectedIntent.intentId)}>
                    {localize(isNeedyIntent ? messages.needyIntentLink : messages.greedyIntentLink)} {selectedIntent.intentName}
                </StyledLink>
            </Text>

            <div>
                <Row>
                    <Label>
                        {localize(messages.fewestUtterancesLabel)}
                        <IntentName>({needyIntent.intentName})</IntentName>
                    </Label>
                    <Stats customProps={{ bold: true }}>{needyIntent.numUtterances}</Stats>
                </Row>
                <Row>
                    <Label>{localize(messages.mean)}</Label>
                    <Stats customProps={{ bold: true }}>{mean.toFixed(1)}</Stats>
                </Row>
                <Row>
                    <Label>
                        {localize(messages.mostUtterancesLabel)} <IntentName>({greedyIntent.intentName})</IntentName>
                    </Label>
                    <Stats customProps={{ bold: true }}>{greedyIntent.numUtterances}</Stats>
                </Row>
            </div>
        </TitledSection>
    );
};
